<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { useAuthStore } from '@/store/auth.store';
import { clearToken, rolesArray } from '@/refs/common.refs';
import { useSidebar } from '@/composables/useSidebar';
import { useI18n } from 'vue-i18n';

// Rôle restreint : un utilisateur dont analytics_viewer est l'UNIQUE rôle ne
// voit que le menu Analytics dans la sidebar. Un rôle cumulé (ex: en plus de
// store_admin) garde l'accès complet correspondant à ses autres rôles.
const isAnalyticsViewerOnly = computed(() => {
  const roles = (rolesArray.value ?? []) as string[];
  return roles.length === 1 && roles[0] === 'analytics_viewer';
});

const emit = defineEmits<{ (e: 'close'): void }>();
const { collapsed } = useSidebar();
const { t } = useI18n({ useScope: 'global' });

function toggleCollapse() {
  if (window.innerWidth < 768) {
    emit('close');
  } else {
    collapsed.value = !collapsed.value;
  }
}

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const queryClient = useQueryClient();

useQuery({
  queryKey: ['restaurant'],
  queryFn: () => authStore.getMeInfo(),
  staleTime: 5 * 60 * 1000,
});

type NavItem = {
  icon: string;
  labelKey: string;
  path: string;
  badge?: string;
  notif?: number;
};
type NavGroupDef = { groupKey: string; items: NavItem[] };

const NAV_DEF: NavGroupDef[] = [
  {
    groupKey: 'pilotage',
    items: [
      { icon: 'pi-th-large', labelKey: 'dashboard', path: '/' },
      { icon: 'pi-chart-line', labelKey: 'analytics', path: '/analytics' },
    ],
  },
  {
    groupKey: 'clients',
    items: [
      { icon: 'pi-users', labelKey: 'clients', path: '/clients' },
      { icon: 'pi-shop', labelKey: 'commerces', path: '/commerces' },
      { icon: 'pi-shopping-bag', labelKey: 'commandes', path: '/commandes' },
      {
        icon: 'pi-wallet',
        labelKey: 'gestionPaiement',
        path: '/gestion-paiement',
      },
      { icon: 'pi-id-card', labelKey: 'usersRoles', path: '/users-roles' },
    ],
  },
  {
    groupKey: 'configuration',
    items: [
      {
        icon: 'pi-objects-column',
        labelKey: 'modules',
        path: '/gestion-modules',
      },
      { icon: 'pi-images', labelKey: 'medias', path: '/medias' },
      { icon: 'pi-desktop', labelKey: 'equipment', path: '/equipements' },
      { icon: 'pi-users', labelKey: 'users', path: '/gestion-utilisateurs' },
      {
        icon: 'pi-clock',
        labelKey: 'disponibilite',
        path: '/commerces/disponibilite',
      },
      {
        icon: 'pi-language',
        labelKey: 'traductions',
        path: '/commerces/traductions',
      },
    ],
  },
  {
    groupKey: 'parametres',
    items: [
      {
        icon: 'pi-credit-card',
        labelKey: 'paiement',
        path: '/settings/payment',
      },
      { icon: 'pi-file-edit', labelKey: 'cgu', path: '/settings/terms' },
    ],
  },
];

const NAV = computed(() => {
  const source = isAnalyticsViewerOnly.value
    ? NAV_DEF.map((g) => ({
        ...g,
        items: g.items.filter((item) => item.labelKey === 'analytics'),
      })).filter((g) => g.items.length)
    : NAV_DEF;

  return source.map((g) => ({
    group: t(`sidebar.groups.${g.groupKey}`),
    groupKey: g.groupKey,
    items: g.items.map((item) => ({
      ...item,
      label: t(`sidebar.items.${item.labelKey}`),
    })),
  }));
});

// Les 3 premiers groupes ouverts par défaut, les autres fermés
const openGroups = ref<Record<string, boolean>>(
  Object.fromEntries(NAV_DEF.map((g, i) => [g.groupKey, i < 3])),
);

function toggleGroup(groupKey: string) {
  if (collapsed.value) return;
  openGroups.value[groupKey] = !openGroups.value[groupKey];
}

function isActive(path: string) {
  if (path === '/') return route.path === '/' && !route.query.section;
  if (path === '/commerces/disponibilite')
    return route.path === '/commerces/disponibilite';
  if (path === '/commerces/traductions')
    return route.path === '/commerces/traductions';
  if (path.startsWith('/settings/') || path.startsWith('/admin/'))
    return route.path === path;
  return route.fullPath.includes(path.replace('/?section=', 'section='));
}

function navigate(path: string) {
  router.push(path);
  if (window.innerWidth < 768) emit('close');
}
function onNavClick() {
  if (window.innerWidth < 768) emit('close');
}

function logout() {
  clearToken();
  rolesArray.value = [];
  // Sinon les données mises en cache sous le rôle précédent (ex:
  // analytics_viewer, données scopées/limitées) sont resservies telles
  // quelles à la prochaine connexion tant que leur staleTime n'est pas
  // écoulé, jusqu'à ce qu'un rechargement de page les vide de force.
  queryClient.clear();
  router.push({ name: 'Login' });
}
</script>

<template>
  <aside
    class="sidebar"
    :class="collapsed ? 'sidebar--collapsed' : 'sidebar--expanded'"
  >
    <!-- Brand -->
    <div
      class="sidebar__brand"
      @click="toggleCollapse"
      role="button"
      :title="collapsed ? t('sidebar.expand') : t('sidebar.collapse')"
    >
      <div class="sidebar__brand-icon">N</div>
      <Transition name="fade-text">
        <div v-if="!collapsed" class="sidebar__brand-text">
          <h1 class="sidebar__brand-name">Nextepay</h1>
          <p class="sidebar__brand-sub">Admin Portal</p>
        </div>
      </Transition>
    </div>

    <!-- Bouton toggle flottant — toujours visible sur le bord droit du sidebar -->
    <button
      class="sidebar__toggle-btn"
      :class="
        collapsed
          ? 'sidebar__toggle-btn--collapsed'
          : 'sidebar__toggle-btn--expanded'
      "
      @click="toggleCollapse"
      :title="collapsed ? t('sidebar.openMenu') : t('sidebar.closeMenu')"
    >
      <i :class="collapsed ? 'pi pi-chevron-right' : 'pi pi-chevron-left'" />
    </button>

    <!-- Nav -->
    <nav class="sidebar__nav">
      <div
        v-for="section in NAV"
        :key="section.groupKey"
        class="sidebar__group"
      >
        <button
          v-if="!collapsed"
          class="sidebar__group-btn"
          @click="toggleGroup(section.groupKey)"
        >
          <span class="sidebar__group-label">{{ section.group }}</span>
          <i
            class="pi sidebar__group-chevron"
            :class="
              openGroups[section.groupKey]
                ? 'pi-chevron-down'
                : 'pi-chevron-right'
            "
          />
        </button>

        <Transition name="nav-collapse">
          <ul v-show="collapsed || openGroups[section.groupKey]">
            <li v-for="item in section.items" :key="item.path">
              <!-- Route virtuelle /__nav__/* : passe toujours par la liste des commerces -->
              <button
                v-if="item.path.startsWith('/__nav__/')"
                class="sidebar__item"
                :class="{ 'sidebar__item--active': isActive(item.path) }"
                @click="navigate(item.path)"
              >
                <i :class="`pi ${item.icon} sidebar__item-icon`" />
                <span v-if="!collapsed" class="sidebar__item-label">{{
                  item.label
                }}</span>
                <span v-if="!collapsed && item.badge" class="sidebar__badge">{{
                  item.badge
                }}</span>
                <span v-if="!collapsed && item.notif" class="sidebar__notif">{{
                  item.notif
                }}</span>
              </button>
              <!-- Route réelle : RouterLink natif (prefetch, historique, clic-molette) -->
              <RouterLink
                v-else
                :to="item.path"
                class="sidebar__item"
                :class="{ 'sidebar__item--active': isActive(item.path) }"
                @click="onNavClick"
              >
                <i :class="`pi ${item.icon} sidebar__item-icon`" />
                <span v-if="!collapsed" class="sidebar__item-label">{{
                  item.label
                }}</span>
                <span v-if="!collapsed && item.badge" class="sidebar__badge">{{
                  item.badge
                }}</span>
                <span v-if="!collapsed && item.notif" class="sidebar__notif">{{
                  item.notif
                }}</span>
              </RouterLink>
            </li>
          </ul>
        </Transition>
      </div>
    </nav>

    <!-- Footer -->
    <div class="sidebar__footer">
      <button class="sidebar__item sidebar__item--logout" @click="logout">
        <i class="pi pi-sign-out sidebar__item-icon" />
        <span v-if="!collapsed" class="sidebar__item-label">{{
          t('sidebar.logout')
        }}</span>
      </button>
    </div>
  </aside>
</template>

<style scoped>
/* ── Sidebar — thème Liquid Glass Navy ── */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 50;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: #0b1320;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  padding: 2rem 1.5rem 1.5rem;
  overflow: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  transition:
    width 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
.sidebar::-webkit-scrollbar {
  display: none;
}
.sidebar--expanded {
  width: 260px;
}
.sidebar--collapsed {
  width: 72px;
  padding-left: 0.875rem;
  padding-right: 0.875rem;
}

@media (max-width: 767px) {
  .sidebar {
    transform: translateX(-100%);
    width: 260px !important;
  }
  .sidebar--mobile-open {
    transform: translateX(0);
  }
}

/* Brand */
.sidebar__brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.375rem;
  margin: -0.375rem -0.375rem 1.75rem;
  border-radius: 0.75rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.sidebar__brand:hover {
  background: rgba(255, 255, 255, 0.05);
}
.sidebar--collapsed .sidebar__brand {
  justify-content: center;
  gap: 0;
}

.sidebar__brand-icon {
  width: 2.5rem;
  height: 2.5rem;
  min-width: 2.5rem;
  background: #ffd60a;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0b1320;
  font-size: 1.25rem;
  font-weight: 800;
  flex-shrink: 0;
}
.sidebar__brand-text {
  overflow: hidden;
}
.sidebar__brand-name {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #ffffff;
  white-space: nowrap;
  letter-spacing: -0.02em;
}
.sidebar__brand-sub {
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
}

/* Nav */
.sidebar__nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.sidebar__group ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  overflow: hidden;
}

/* Group header */
.sidebar__group-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.5rem;
  margin-bottom: 0.375rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
}
.sidebar__group-label {
  font-size: 0.5625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: rgba(255, 255, 255, 0.2);
  white-space: nowrap;
}
.sidebar__group-chevron {
  font-size: 0.5rem;
  color: rgba(255, 255, 255, 0.2);
  transition: transform 0.2s;
}

/* Accordion */
.nav-collapse-enter-active,
.nav-collapse-leave-active {
  transition:
    opacity 0.2s ease,
    max-height 0.25s ease;
  max-height: 300px;
  overflow: hidden;
}
.nav-collapse-enter-from,
.nav-collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

/* Nav items */
.sidebar__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: none;
  background: none;
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-decoration: none;
  transition:
    background 0.15s,
    color 0.15s;
}
.sidebar__item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
}
.sidebar__item--active {
  background: rgba(255, 255, 255, 0.1);
  color: #ffd60a;
  font-weight: 700;
}
.sidebar__item--logout:hover {
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.08);
}
.sidebar__item-icon {
  font-size: 1.125rem;
  min-width: 1.125rem;
}
.sidebar__item-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Toggle btn */
.sidebar__toggle-btn {
  position: fixed;
  top: 1.375rem;
  z-index: 51;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #1e2a3a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5625rem;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition:
    left 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.15s,
    color 0.15s;
}
.sidebar__toggle-btn--expanded {
  left: calc(260px - 0.75rem);
}
.sidebar__toggle-btn--collapsed {
  left: calc(72px - 0.75rem);
}
.sidebar__toggle-btn:hover {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
@media (max-width: 767px) {
  .sidebar__toggle-btn {
    display: none;
  }
}

/* Fade text */
.fade-text-enter-active,
.fade-text-leave-active {
  transition: opacity 0.15s;
}
.fade-text-enter-from,
.fade-text-leave-to {
  opacity: 0;
}

/* Badge & Notif */
.sidebar__badge {
  font-size: 0.5625rem;
  font-weight: 700;
  padding: 0.15rem 0.375rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
}
.sidebar__notif {
  width: 1.125rem;
  height: 1.125rem;
  min-width: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ef4444;
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 700;
  border-radius: 9999px;
}

/* Footer */
.sidebar__footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

/* Shimmer (conservé pour compatibilité) */
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<style>
[data-theme='dark'] .sidebar {
  background: #080e18;
}
</style>
